import { forwardRef, memo, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { CountrySelector } from '@components/country-selector';
import { Input } from '@components/input';
import { COUNTRIES } from '@constants/countries';
import classNames from 'classnames';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

import { InputPhoneProps } from './input-phone.type';

// A box holding only the dial code reads as empty, so the value it reports must be empty too.
const isDialCodeOnly = (phone: string, dialCode: string) => {
  const digits = phone.replace(/\D/g, '');
  return digits === '' || digits === dialCode.replace(/\D/g, '');
};

const EMPTY_CHANGE = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;

export const InputPhone = memo(
  forwardRef(
    (
      {
        name,
        value,
        placeholder,
        selectFontLight,
        initialCountry = 'adsfasfasdf',
        onChange,
        disabled,
        hasError,
        showDial,
        ...rest
      }: InputPhoneProps,
      ref: Ref<HTMLInputElement>
    ) => {
      const initialPhoneNumberCountry = value && parsePhoneNumberFromString(value)?.country;
      const initialCountryObject = initialPhoneNumberCountry
        ? COUNTRIES[initialPhoneNumberCountry]
        : COUNTRIES[initialCountry.toUpperCase()];
      const [country, setCountry] = useState(initialCountryObject);
      const [phoneValue, setPhoneValue] = useState(value);
      const asYouTypeRef = useRef(new AsYouType());
      const asYouType = asYouTypeRef.current;

      const handleCountryChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
          const selection: string = e.target.value;
          const countrySelected = COUNTRIES[selection];
          setCountry(countrySelected);
          setPhoneValue(countrySelected.dialCode);
          onChange(EMPTY_CHANGE);
        },
        [setCountry, setPhoneValue, onChange]
      );

      const normalizePhoneValue = useCallback(
        (rawPhoneValue: string, e: React.ChangeEvent<HTMLInputElement>) => {
          asYouType.reset();
          let formattedPhone = asYouType.input(rawPhoneValue) as string;
          // Check if dial code is missing and prepend if necessary
          if (!formattedPhone.startsWith(country.dialCode)) {
            formattedPhone = country.dialCode + formattedPhone;
          }

          setPhoneValue(formattedPhone);
          onChange(e);
        },
        [asYouType, setPhoneValue, onChange, country.dialCode]
      );

      const handlePhoneChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          const phone = e.target.value.toString();

          if (!phone.startsWith(country.dialCode)) {
            const correctedValue = country.dialCode + phone.replace(/[^\d]+/g, '');
            e.target.value = correctedValue;
          }
          if (isDialCodeOnly(e.target.value, country.dialCode)) {
            setPhoneValue(country.dialCode);
            onChange(EMPTY_CHANGE);
            return;
          }
          normalizePhoneValue(phone, e);
        },
        [country, normalizePhoneValue, onChange]
      );

      useEffect(() => {
        if (initialCountry) {
          if (value) {
            const parsedPhone = parsePhoneNumberFromString(value);
            if (!!parsedPhone && parsedPhone.isValid()) {
              if (country.id !== parsedPhone.country && parsedPhone.country) {
                setCountry(COUNTRIES[parsedPhone.country]);
              }
              asYouType.reset();
              setPhoneValue(asYouType.input(value));
              return;
            }
            // Keep an invalid stored number visible so it can be fixed, instead of hiding it behind the dial code.
            setPhoneValue(value);
            return;
          }
          setPhoneValue(country.dialCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return (
        <div className='flex gap-2'>
          <div
            className={classNames({
              'w-36': showDial,
              'w-16 shrink-0': !showDial,
            })}>
            <CountrySelector
              initialCountry={country.id}
              onChange={handleCountryChange}
              disabled={disabled}
              showDial={showDial}
              selectFontLight={selectFontLight}
            />
          </div>
          <div className='grow'>
            <Input
              {...rest}
              ref={ref}
              type='tel'
              onChange={handlePhoneChange}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  if (!!phoneValue && phoneValue.length <= country.dialCode.length) {
                    setPhoneValue('');
                    onChange(EMPTY_CHANGE);
                  }
                }
              }}
              placeholder={placeholder}
              name={name}
              hasError={hasError}
              value={phoneValue}
              disabled={disabled}
            />
          </div>
        </div>
      );
    }
  )
);
